import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { AdminShell } from "@/components/admin/admin-shell";
import { AdminAuthProvider, readStoredSecret } from "@/contexts/AdminAuthContext";
import { AdminUiProvider } from "@/contexts/AdminUiContext";

/**
 * Blocks the admin section unless an administrator secret is already stored.
 *
 * This replaces the router's `beforeLoad` redirect. It is a convenience gate,
 * not a security boundary — every admin request carries the secret and the
 * backend is what actually enforces it.
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  if (!readStoredSecret()) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
}

export function AdminLayout() {
  const { pathname } = useLocation();
  const isLogin = pathname.startsWith("/admin/login");

  return (
    <AdminAuthProvider>
      <AdminUiProvider>
        {isLogin ? (
          <Outlet />
        ) : (
          <AdminShell>
            <Outlet />
          </AdminShell>
        )}
      </AdminUiProvider>
    </AdminAuthProvider>
  );
}
